import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Avatar from '../components/Avatar';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../config';
import './MyProfilePage.css';

// Utility to check JWT expiry
function isTokenExpired(token) {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return true;
      return Date.now() >= payload.exp * 1000;
    } catch  {   
      return true;
    }
  }
  // Local RoamShare username validation (same rule as mobile)
function validateUsernameLocal(u) {
  const trimmed = (u || '').trim().toLowerCase();
  const re = /^[a-z0-9._]{3,30}$/;

  if (!trimmed) return 'Pick a username for RoamShare.';
  if (!re.test(trimmed)) {
    return '3–30 lowercase letters, numbers, dots, or underscores.';
  }
  return '';
}

const MyProfilePage = () => {
    const { user, isLoggedIn, accessToken,  handleLogout } = useAuth();
    const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
    const [editing, setEditing] = React.useState(false);
    const [newUsername, setNewUsername] = React.useState('');
    const [usernameError, setUsernameError] = React.useState('');
    const [roamProfile, setRoamProfile] = React.useState(null);
    const [selectedFile, setSelectedFile] = React.useState(null);
    const [profilePicTimestamp, setProfilePicTimestamp] = React.useState(Date.now());
    const [removingProfilePicture, setRemovingProfilePicture] = React.useState(false);
    const [ setEditingBio] = React.useState(false);
    const [bioInput, setBioInput] = React.useState('');
    const [showEditModal, setShowEditModal] = React.useState(false);
    const [myPosts, setMyPosts] = useState([]);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [activeImageIndexes, setActiveImageIndexes] = useState({});
    const [followers, setFollowers] = useState([]);
    const [following, setFollowing] = useState([]);
    const [showFollowModal, setShowFollowModal] = useState(false);
    const [followModalType, setFollowModalType] = useState('followers');
    const [searchTerm, setSearchTerm] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [likeStates, setLikeStates] = useState({});
    const [likesReady, setLikesReady] = useState(false);
    const pendingLikesRef = React.useRef(new Set());
    const [expandedPost, setExpandedPost] = useState(null);
    const [modalImageIndex, setModalImageIndex] = useState(0);
    const [postComments, setPostComments] = useState([]);
    const [commentOffset, setCommentOffset] = useState(0);
    const [hasMoreComments, setHasMoreComments] = useState(false);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentsError, setCommentsError] = useState('');
    const [newComment, setNewComment] = useState('');
    const [commentSubmitting, setCommentSubmitting] = useState(false);
    const [commentSubmitError, setCommentSubmitError] = useState('');
    const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);
    const [loadedReplies, setLoadedReplies] = useState({});
    const [repliesLoading, setRepliesLoading] = useState({});
    const [replyErrors, setReplyErrors] = useState({});
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyText, setReplyText] = useState('');
    const commentSubmittingRef = React.useRef(false);
    const currentExpandedPostRef = React.useRef(null);
    const postViewSequenceRef = React.useRef(0);
    currentExpandedPostRef.current = expandedPost;
    const openPost = (post) => {
      let images = [];
    
      try {
        const parsed =
          typeof post.images === 'string'
            ? JSON.parse(post.images)
            : post.images;
    
        images = Array.isArray(parsed) ? parsed : [];
      } catch {
        images = [];
      }
    
      setModalImageIndex(0);
      setPostComments([]);
      setCommentOffset(0);
      setHasMoreComments(false);
      setCommentsError('');
      setCommentsLoading(true);
      setOpenDropdownId(null);
      setNewComment('');
      setCommentSubmitError('');
      setLoadedReplies({});
setRepliesLoading({});
setReplyErrors({});
setReplyingTo(null);
setReplyText('');

setExpandedPost({
  ...post,
  post_id: post.post_id ?? post.id,
  viewId: ++postViewSequenceRef.current,
  images,
});
    };
    
    const closePost = () => {
      setExpandedPost(null);
    };
    const fetchReplies = async (commentId) => {
      const post = expandedPost;
      if (!post) return;
    
      setRepliesLoading((current) => ({
        ...current,
        [commentId]: true,
      }));
    
      setReplyErrors((current) => ({
        ...current,
        [commentId]: '',
      }));
    
      try {
        const response = await axios.get(
          api.url(
            `/api/roam-share-posts/comments/${commentId}/replies`
          ),
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
    
        if (currentExpandedPostRef.current?.viewId !== post.viewId) return;
    
        setLoadedReplies((current) => ({
          ...current,
          [commentId]: Array.isArray(response.data)
            ? response.data
            : [],
        }));
      } catch {
        if (currentExpandedPostRef.current?.viewId === post.viewId) {
          setReplyErrors((current) => ({
            ...current,
            [commentId]: 'Could not load replies. Please try again.',
          }));
        }
      } finally {
        if (currentExpandedPostRef.current?.viewId === post.viewId) {
          setRepliesLoading((current) => ({
            ...current,
            [commentId]: false,
          }));
        }
      }
    };
    
    const handleAddReply = async (commentId) => {
      const post = expandedPost;
      const text = replyText.trim();
    
      if (
        !post ||
        !text ||
        !accessToken ||
        commentSubmittingRef.current
      ) {
        return;
      }
    
      commentSubmittingRef.current = true;
      setCommentSubmitting(true);
    
      setReplyErrors((current) => ({
        ...current,
        [commentId]: '',
      }));
    
      try {
        await axios.post(
          api.url(
            `/api/roam-share-posts/${post.post_id}/comments/${commentId}/reply`
          ),
          { comment_text: text },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
    
        // Count the new reply on the profile card.
        setMyPosts((posts) =>
          posts.map((item) =>
            String(item.post_id ?? item.id) === String(post.post_id)
              ? {
                  ...item,
                  comments_count:
                    (Number(item.comments_count) || 0) + 1,
                }
              : item
          )
        );
    
        if (currentExpandedPostRef.current?.viewId !== post.viewId) return;
    
        setReplyText('');
        setReplyingTo(null);
    
        // Reload the thread so the newly saved reply appears.
        await fetchReplies(commentId);
    
        if (currentExpandedPostRef.current?.viewId !== post.viewId) return;
    
        setPostComments((comments) =>
          comments.map((comment) =>
            String(comment.comment_id) === String(commentId)
              ? {
                  ...comment,
                  replies_count:
                    (Number(comment.replies_count) || 0) + 1,
                }
              : comment
          )
        );
    
        setExpandedPost((current) =>
          current?.viewId === post.viewId
            ? {
                ...current,
                comments_count:
                  (Number(current.comments_count) || 0) + 1,
              }
            : current
        );
      } catch {
        if (currentExpandedPostRef.current?.viewId === post.viewId) {
          setReplyErrors((current) => ({
            ...current,
            [commentId]: 'Could not post your reply. Please try again.',
          }));
        }
      } finally {
        commentSubmittingRef.current = false;
        setCommentSubmitting(false);
      }
    };
    const handleAddComment = async () => {
      const text = newComment.trim();
      const post = expandedPost;
    
      if (
        !post ||
        !text ||
        !accessToken ||
        commentSubmittingRef.current
      ) {
        return;
      }
    
      const postId = post.post_id;
    
      commentSubmittingRef.current = true;
      setCommentSubmitting(true);
      setCommentSubmitError('');
    
      try {
        await axios.post(
          api.url(`/api/roam-share-posts/${postId}/comments`),
          { comment_text: text },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
    
        // Update the count on the profile card.
        setMyPosts((posts) =>
          posts.map((item) =>
            String(item.post_id ?? item.id) === String(postId)
              ? {
                  ...item,
                  comments_count:
                    (Number(item.comments_count) || 0) + 1,
                }
              : item
          )
        );
    
        // Only update the open view if it is still the same view.
        if (currentExpandedPostRef.current?.viewId === post.viewId) {
          setExpandedPost((current) =>
            current?.viewId === post.viewId
              ? {
                  ...current,
                  comments_count:
                    (Number(current.comments_count) || 0) + 1,
                }
              : current
          );
    
          setNewComment('');
          setPostComments([]);
          setCommentsLoading(true);
          setCommentOffset(0);
          setCommentsRefreshKey((key) => key + 1);
        }
      } catch {
        if (currentExpandedPostRef.current?.viewId === post.viewId) {
          setCommentSubmitError(
            'Could not post your comment. Please try again.'
          );
        }
      } finally {
        commentSubmittingRef.current = false;
        setCommentSubmitting(false);
      }
    };
    // Load five comments at a time.
   
    useEffect(() => {
      const postId = expandedPost?.post_id;
      if (postId == null) return;
      
      const controller = new AbortController();
    
      const loadComments = async () => {
        setCommentsLoading(true);
        setCommentsError('');
    
        try {
          const response = await axios.get(
            api.url(
             `/api/roam-share-posts/${postId}/comments?limit=5&offset=${commentOffset}`
            ),
            {
              headers: { Authorization: `Bearer ${accessToken}` },
              signal: controller.signal,
            }
          );
    
          if (controller.signal.aborted) return;
    
          const incoming = Array.isArray(response.data)
            ? response.data
            : [];
    
          setPostComments((previous) => {
            if (commentOffset === 0) return incoming;
    
            const existingIds = new Set(
              previous.map((comment) => String(comment.comment_id))
            );
    
            return [
              ...previous,
              ...incoming.filter(
                (comment) =>
                  !existingIds.has(String(comment.comment_id))
              ),
            ];
          });
    
          setHasMoreComments(incoming.length === 5);
        } catch {
          if (!controller.signal.aborted) {
            setCommentsError('Could not load comments. Close and reopen the post to retry.');
          }
        } finally {
          if (!controller.signal.aborted) {
            setCommentsLoading(false);
          }
        }
      };
    
      loadComments();
    
      return () => controller.abort();
    }, [
      expandedPost?.post_id,
      commentOffset,
      accessToken,
      commentsRefreshKey,
    ]);
    
    // Lock background scrolling and support Escape to close.
    useEffect(() => {
      if (expandedPost?.post_id == null) return;

const previousOverflow = document.body.style.overflow;
      const previousFocus = document.activeElement;
    
      document.body.style.overflow = 'hidden';
    
      const handleEscape = (event) => {
        if (event.key === 'Escape') {
          setExpandedPost(null);
        }
      };
    
      document.addEventListener('keydown', handleEscape);
    
      return () => {
        document.body.style.overflow = previousOverflow;
        document.removeEventListener('keydown', handleEscape);
    
        if (previousFocus instanceof HTMLElement) {
          previousFocus.focus();
        }
      };
    }, [expandedPost?.post_id]);
    const goToPrevImage = (postId, images) => {
      setActiveImageIndexes(prev => ({
        ...prev,
        [postId]: ((prev[postId] ?? 0) - 1 + images.length) % images.length,
      }));
    };
    
    const goToNextImage = (postId, images) => {
      setActiveImageIndexes(prev => ({
        ...prev,
        [postId]: ((prev[postId] ?? 0) + 1) % images.length,
      }));
    };
    const handleTogglePostLike = async (post) => {
      const postId = post.post_id ?? post.id;
      const requestKey = String(postId);
    
      if (
        postId == null ||
        !likesReady ||
        pendingLikesRef.current.has(requestKey)
      ) {
        return;
      }
    
      pendingLikesRef.current.add(requestKey);
    
      const previous = likeStates[postId] ?? {
        liked: false,
        count: Number(post.likes) || 0,
      };
    
      const next = {
        liked: !previous.liked,
        count: Math.max(
          0,
          previous.count + (previous.liked ? -1 : 1)
        ),
      };
    
      // Update the icon and count immediately.
      setLikeStates((current) => ({
        ...current,
        [postId]: next,
      }));
    
      try {
        await axios.post(
          api.url(`/api/roam-share-posts/like/${postId}`),
          {},
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
    
        setMyPosts((current) =>
          current.map((item) =>
            String(item.post_id ?? item.id) === requestKey
              ? { ...item, likes: next.count }
              : item
          )
        );
      } catch {
        // Restore the previous state if saving fails.
        setLikeStates((current) => ({
          ...current,
          [postId]: previous,
        }));
    
        window.alert('Could not update your like. Please try again.');
      } finally {
        pendingLikesRef.current.delete(requestKey);
      }
    };
    const handleDeletePost = async (postId) => {
      if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    
      try {
        const response = await axios.delete( api.url(`/api/roam-share-posts/delete/${postId}`),
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
    
       
    
        // Remove post from frontend state
        setMyPosts((prev) =>
          prev.filter((post) => post.id !== postId && post.post_id !== postId)
        );
      } catch  {
        alert('Something went wrong. Try again.');
      }
    };
    
        const handleSaveUsername = async () => {
      const trimmed = (newUsername || '').trim().toLowerCase();

      // 1) Local format validation (same as mobile)
      const localErr = validateUsernameLocal(trimmed);
      if (localErr) {
        setUsernameError(localErr);
        return;
      }

      try {
        setUsernameError('');

        // 2) Check availability + reserved usernames via backend
        const checkRes = await fetch(
          api.url(`/api/roam-share-posts/check-username?u=${encodeURIComponent(trimmed)}`),
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!checkRes.ok) {
          setUsernameError('Could not check username right now. Please try again.');
          return;
        }

        const checkData = await checkRes.json();
        // Expected shape: { available: boolean, reason?: "invalid" }

        if (checkData.available === false && checkData.reason === 'invalid') {
          // 🔹 RESERVED / disallowed username from backend
          setUsernameError('This username is not allowed.');
          return;
        }

        if (checkData.available === false) {
          setUsernameError('Username already taken.');
          return;
        }

        // 3) If we reach here, username is available → save it
        const response = await fetch(api.url('/api/roam-share-posts/set-username'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ username: trimmed }),
        });

        if (response.ok) {
          // ✅ Refetch the profile now
          const profileResponse = await fetch(api.url('/api/roam-share-posts/my-profile'), {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });

          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            setRoamProfile(profileData);
            setEditing(false);
            setUsernameError('');
          } else {
            setUsernameError('Failed to refresh profile.');
          }
        } else {
          setUsernameError('Username already taken or invalid.');
        }
      } catch {
        setUsernameError('Something went wrong. Try again.');
      }
    };

      const handleProfilePicUpload = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
          
          return;
        }
        const formData = new FormData();
        formData.append('profile_picture', selectedFile);
      
        try {
          const res = await fetch(api.url('/api/roam-share-posts/upload-profile-picture'), {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          });
      
          if (res.ok) {
            const refreshed = await fetch(api.url('/api/roam-share-posts/my-profile'), {
              headers: { Authorization: `Bearer ${accessToken}` },
            });
      
            if (refreshed.ok) {
              const updatedProfile = await refreshed.json();
      
              setRoamProfile(updatedProfile);
              setSelectedFile(null);
              setProfilePicTimestamp(Date.now());
             }
      } else {
        await res.text(); // no logging; just read to avoid unhandled promise
      }
    } catch {
      // silent in production
    }
  };
  const handleRemoveProfilePicture = async () => {
    if (!roamProfile?.profile_picture || removingProfilePicture) {
      return;
    }
  
    const confirmed = window.confirm(
      'Are you sure you want to remove your profile picture?'
    );
  
    if (!confirmed) {
      return;
    }
  
    try {
      setRemovingProfilePicture(true);
  
      const response = await fetch(
        api.url('/api/roam-share-posts/remove-profile-picture'),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
  
      const data = await response.json().catch(() => ({}));
  
      if (!response.ok) {
        throw new Error(
          data?.error || 'Could not remove profile picture.'
        );
      }
  
      setRoamProfile((previousProfile) =>
        previousProfile
          ? {
              ...previousProfile,
              profile_picture: null,
            }
          : previousProfile
      );
  
      setSelectedFile(null);
      setProfilePicTimestamp(Date.now());
    } catch (err) {
      window.alert(
        err?.message ||
          'Could not remove profile picture. Please try again.'
      );
    } finally {
      setRemovingProfilePicture(false);
    }
  };  
      const handleSaveBio = async () => {
        if (!bioInput.trim()) return;
      
        try {
          const response = await fetch(api.url('/api/roam-share-posts/update-description'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ bio: bioInput }),
          });
      
          if (response.ok) {
            const updated = await response.json();
            setRoamProfile(prev => ({ ...prev, bio: updated.bio }));
            setEditingBio(false);
         }
    } catch {
      // silent in production
    }
  };
      
    const navigateAndLogout = async () => {
        await handleLogout();
        navigate('/login');
      };
      

  const navigate = useNavigate();
  const location = useLocation();
   
  const handleOpenFollowModal = async (type) => {
    try {
      const userId = user?.id || user?.user_id;
  
      if (!userId) {
        
        return;
      }
  
      const response = await axios.get(api.url(`/api/roam-share-posts/${type}/${userId}`));
      if (type === 'followers') {
        setFollowers(response.data);
      } else {
        setFollowing(response.data);
      }
  
      setFollowModalType(type);
      setShowFollowModal(true);
    } catch {
      
    }
  };
  
  
  useEffect(() => {
    if (!isLoggedIn || isTokenExpired(accessToken)) {
      navigate('/login', { state: { from: location.pathname } });
    }
    
  }, [isLoggedIn, accessToken, navigate, location.pathname]);

  useEffect(() => {
    const fetchRoamShareProfile = async () => {
      try {
        const response = await fetch(api.url('/api/roam-share-posts/my-profile'), {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
  
        if (response.ok) {
            const data = await response.json();
            
            setRoamProfile(data); // ✅ store the roam profile
            }
      } catch {
        // silent in production
      }
    };
  
    if (accessToken) {
      fetchRoamShareProfile();
    }
  }, [accessToken]);
  useEffect(() => {
    if (!accessToken) return;
  
    const fetchMyPosts = async () => {
      try {
        const res = await axios.get(
          api.url('/api/roam-share-posts/my-posts'),
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const posts = res.data;
setMyPosts(posts);
setLikesReady(false);

try {
  const likedResponse = await axios.get(
    api.url('/api/roam-share-posts/liked-posts'),
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );

  // Normalize IDs so a numeric ID also matches a string ID.
  const likedIds = new Set(likedResponse.data.map(String));
  const initialLikes = {};

  posts.forEach((post) => {
    const postId = post.post_id ?? post.id;

    initialLikes[postId] = {
      liked: likedIds.has(String(postId)),
      count: Number(post.likes) || 0,
    };
  });

  setLikeStates(initialLikes);
  setLikesReady(true);
} catch {
  window.alert('Could not load your likes. Please refresh the page to retry.');
}
  
        // ─── Initialize carousel indexes ───────────────────────
        const initialIndexes = {};
        posts.forEach(post => {
          const id = post.post_id || post.id;
          initialIndexes[id] = 0;
        });
        setActiveImageIndexes(initialIndexes);
        // ────────────────────────────────────────────────────────
  
      } catch  {
        
      }
    };
  
    fetchMyPosts();
  }, [accessToken]);
  useEffect(() => {
    const fetchFollowData = async () => {
      if (!roamProfile?.user_id) return;
  
      try {
        const followersRes = await axios.get(api.url(`/api/roam-share-posts/followers/${roamProfile.user_id}`));
        const followingRes = await axios.get(api.url(`/api/roam-share-posts/following/${roamProfile.user_id}`));

        setFollowers(followersRes.data);
        setFollowing(followingRes.data);
      } catch  {
      }
    };
  
    fetchFollowData();
  }, [roamProfile]);
  
  
  
  if (!isLoggedIn) return null;
  const handleEditPost = (post) => {
  try {
    const postType = post.post_type || 'photo';

    const imagesArray = postType === 'photo'
      ? (Array.isArray(post.images)
          ? post.images
          : (typeof post.images === 'string'
              ? (() => { try { return JSON.parse(post.images); } catch { return []; } })()
              : []))
      : [];

    const statePayload = {
      isEditing: true,
      postId: post.post_id || post.id,
      postType,  // 'photo' | 'video'
      caption: post.caption || '',
      location: post.location || '',
      tags: post.tags || [],
    };

    if (postType === 'photo') {
      // imagesArray is already array of full URLs from backend
      statePayload.images = imagesArray;
    } else if (postType === 'video') {
      if (post.video) {
        // post.video is already a full URL
        statePayload.video = post.video;
      }
    }

    navigate('/create-post', { state: statePayload });
  } catch {
    // silent
  }
};

  
  return (
    <div className="profile-page">
      <div className="header">
            <div className="brand" onClick={() => navigate("/")}>
            <img src="/images/customroam-logo.png" alt="Logo" className="logo-img" />
            <span className="brand-text">CUSTOM ROAM</span>
             </div>
              {/* Hamburger menu button */}
              <button className="header-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
                ☰
               </button>
             <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
                    <span className="nav-link" onClick={() => navigate('/')}>Home</span>
                    <span className="nav-link" onClick={() => navigate('/roam-share')}>Roam Share</span>
                    <span className="nav-link" onClick={() => navigate('/my-itineraries')}>My Itineraries</span>
                    <span className="notification-icon" onClick={() => navigate('/notifications')}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                       height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                     className="bell-svg"
                    >
                        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                         <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                 </span>
                    <span className="nav-link" onClick={() => navigate('/profile')}>Profile</span>
                </div>
            </div>

      <div className="profile-bar">
      <div className="profile-section">
      <div className="avatar-username">
      <div className="user-avatar">
  <Avatar
  src={
    roamProfile?.profile_picture
      ? `${roamProfile.profile_picture}?t=${profilePicTimestamp}`
      : undefined
  }
  size={112}
  className="profile-avatar"
/>

</div>

  {roamProfile?.username ? (
  <h2 className="username">@{roamProfile.username}</h2>
  ) : (
    <p className="set-username-link" onClick={() => setEditing(true)}>
      + Set your username
    </p>
  )}

{editing && (
  <div className="username-edit">
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <input
        type="text"
        value={newUsername}
        onChange={(e) => setNewUsername(e.target.value)}
        placeholder="Enter a username"
        className="username-input"
      />

      {usernameError && (
        <p style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
          {usernameError}
        </p>
      )}
    </div>

    <button onClick={handleSaveUsername} className="save-username-btn">
  Save
</button>
  </div>
)}

</div>
  <div className="profile-details">
  <p className="bio">
  {roamProfile?.bio?.trim() ? roamProfile.bio : <span className="add-bio-placeholder"> Add Description</span>}
</p>
<div className="follow-counts">
   <div className="count-box" onClick={() => handleOpenFollowModal('followers')} style={{ cursor: 'pointer' }}>
    <strong>{roamProfile?.follower_count || 0}</strong>
    <span>Followers</span>
  </div>
  <div className="count-box" onClick={() => handleOpenFollowModal('following')} style={{ cursor: 'pointer' }}>
    <strong>{roamProfile?.following_count || 0}</strong>
    <span>Following</span>
  </div>
</div>


    <button className="edit-profile-btn" onClick={() => {
  setBioInput(roamProfile?.bio || '');
  setNewUsername(roamProfile?.username || '');
  setShowEditModal(true);
}}>
  Edit Profile
</button>

  </div>
</div>

<div className="profile-right">
<button 
  className="menu-btn" 
  onClick={() => setHeaderMenuOpen(prev => !prev)}
>
  ☰
</button>
{headerMenuOpen && (
  <div className="dropdown-menu">
    <div className="dropdown-item" onClick={() => navigate('/bucket-list')}>Bucket List</div>
    <div className="dropdown-item" onClick={() => navigate('/account-info')}>Account Info</div>
    <div className="dropdown-item" onClick={() => navigate('/help')}>Help</div>
    <div className="dropdown-item" onClick={() => navigate('/privacy-policy')}>Privacy Policy</div>
    <div className="dropdown-item" onClick={() => navigate('/terms')}>Terms & Conditions</div>
    <div className="dropdown-item" onClick={() => navigate('/about-us')}>About Us</div>
    <div className="dropdown-item logout-item" onClick={navigateAndLogout}>Logout</div>
  </div>
)}
  <button className="new-post-btn" onClick={() => navigate('/create-post')}>+ New Post</button>
</div>

      </div>
      <h2 className="my-posts-heading">My Posts</h2>
      <div className="posts-grid">
  {myPosts.length === 0 ? (
    <p>No posts yet. Start sharing your adventures!</p>
  ) : (
    myPosts.map((post) => {
     
      return(
        <div
        key={post.post_id ?? post.id}
        className="post-card"
        onClick={() => openPost(post)}
      >
 <div
  className="post-menu-wrapper"
  onClick={(e) => e.stopPropagation()}
>
    <span
      className="post-menu-icon"
      onClick={() =>
        setOpenDropdownId((prevId) => (prevId === post.post_id ? null : post.post_id))
      }
    >
      ⋯
    </span>
    {openDropdownId === post.post_id && (
      <div className="post-dropdown">
        <div className="post-dropdown-item" onClick={() => handleEditPost(post)}> Edit Post</div>
        <div className="post-dropdown-item" onClick={() => handleDeletePost(post.post_id || post.id)}> Delete Post</div>

      </div>
    )}
  </div>
  {/* ✅ Render video post if it's a video */}
 {post.post_type === 'video' && post.video && (() => {
  // post.video is already a full URL from backend
  const videoBase = post.video;
  const poster = `${videoBase}#t=0.5`;

  return (
    <div
    className="video-wrapper"
    onClick={(e) => e.stopPropagation()}
  >
      <video
        className="roam-post-video"
        controls
        preload="metadata"
        poster={poster}
      >
        <source src={videoBase} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
})()}


{/* ✅ Render image carousel if it's a photo post */}
{post.post_type !== 'video' && post.images && (() => {
  const key = post.post_id ?? post.id;
  let imagesArray = [];

  if (Array.isArray(post.images)) {
    imagesArray = post.images; // already full URLs
  } else if (typeof post.images === 'string') {
    try {
      imagesArray = JSON.parse(post.images);
    } catch {
      imagesArray = [];
    }
  }

  const activeIndex = activeImageIndexes[key] ?? 0;
  if (!imagesArray.length) return null;

  return (
    <div className="carousel-container">
      {imagesArray.length > 1 && (
        <button
          className="carousel-left"
          onClick={(e) => {
            e.stopPropagation();
            goToPrevImage(key, imagesArray);
          }}
        >
          ‹
        </button>
      )}

      <img
        key={`slide-${key}-${activeIndex}`}
        src={imagesArray[activeIndex]}  // use URL directly
        alt=""
        className="post-image"
      />

      {imagesArray.length > 1 && (
        <button
          className="carousel-right"
          onClick={(e) => {
            e.stopPropagation();
            goToNextImage(key, imagesArray);
          }}
        >
          ›
        </button>
      )}
    </div>
  );
})()}
<div className="profile-post-actions">
  <button
    type="button"
    className="profile-post-like-button"
    disabled={!likesReady}
    aria-label={
      likeStates[post.post_id ?? post.id]?.liked
        ? 'Unlike post'
        : 'Like post'
    }
    aria-pressed={
      !!likeStates[post.post_id ?? post.id]?.liked
    }
    onClick={(e) => {
      e.stopPropagation();
      handleTogglePostLike(post);
    }}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width="28"
      height="28"
      aria-hidden="true"
    >
      <path
        d="M32 2C18 2 6 14 6 28c0 7.7 5.5 15.3 12 21.6 4.8 4.5 10 8.4 14 12.4 4-4 9.2-8 14-12.4C52.5 43.3 58 35.7 58 28 58 14 46 2 32 2z"
        fill={
          likeStates[post.post_id ?? post.id]?.liked
            ? '#56ffc7'
            : 'none'
        }
        stroke="#008080"
        strokeWidth="4"
      />
      <path
        d="M32 16c-5-6.5-14-5.5-14 4 0 5 5 10 11 15l3 2.5 3-2.5c6-5 11-10 11-15 0-9.5-9-10.5-14-4z"
        fill={
          likeStates[post.post_id ?? post.id]?.liked
            ? '#e63946'
            : 'none'
        }
        stroke="#e63946"
        strokeWidth="3"
      />
    </svg>

    <span>
      {likeStates[post.post_id ?? post.id]?.count ??
        post.likes ??
        0}
    </span>
  </button>
  <button
  type="button"
  className="profile-post-comment-button"
  aria-label="Open post and comments"
  onClick={(e) => {
    e.stopPropagation();
    openPost(post);
  }}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#008080"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
  </svg>

  <span>{post.comments_count ?? 0}</span>
</button>
</div>
        <p className="post-location">📍 {post.location}</p>
        {post.caption && <p className="post-caption">{post.caption}</p>}
      </div>
      )
})
  )}
</div>
{expandedPost && (
  <div
    className="profile-post-modal-overlay"
    onClick={closePost}
  >
    <div
      className="profile-post-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Post and comments"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="profile-post-modal-close"
        onClick={closePost}
        aria-label="Close post"
        autoFocus
      >
        ✕
      </button>

      <div className="profile-post-modal-media">
        {expandedPost.post_type === 'video' ? (
          <video
            key={expandedPost.post_id}
            src={expandedPost.video}
            controls
            playsInline
            preload="metadata"
            className="profile-post-modal-video"
          />
        ) : expandedPost.images.length > 0 ? (
          <>
            {expandedPost.images.length > 1 && (
              <button
                type="button"
                className="profile-post-modal-prev"
                aria-label="Previous photo"
                onClick={() =>
                  setModalImageIndex(
                    (index) =>
                      (index - 1 + expandedPost.images.length) %
                      expandedPost.images.length
                  )
                }
              >
                ‹
              </button>
            )}

            <img
              src={expandedPost.images[modalImageIndex]}
              alt={expandedPost.location || 'Post photo'}
              className="profile-post-modal-image"
            />

            {expandedPost.images.length > 1 && (
              <button
                type="button"
                className="profile-post-modal-next"
                aria-label="Next photo"
                onClick={() =>
                  setModalImageIndex(
                    (index) =>
                      (index + 1) % expandedPost.images.length
                  )
                }
              >
                ›
              </button>
            )}
          </>
        ) : (
          <p>No media available.</p>
        )}
      </div>

      <div className="profile-post-modal-details">
        <div className="profile-post-modal-header">
          <Avatar
            src={roamProfile?.profile_picture || undefined}
            size={42}
          />

          <div>
            <strong>@{roamProfile?.username}</strong>

            {(expandedPost.caption || expandedPost.content) && (
              <p>
                {expandedPost.caption || expandedPost.content}
              </p>
            )}

            {expandedPost.location && (
              <p>📍 {expandedPost.location}</p>
            )}
          </div>
        </div>
        <div className="profile-expanded-post-actions">
  <button
    type="button"
    className="profile-post-like-button"
    disabled={!likesReady}
    aria-label={
      likeStates[expandedPost.post_id]?.liked
        ? 'Unlike post'
        : 'Like post'
    }
    aria-pressed={
      !!likeStates[expandedPost.post_id]?.liked
    }
    onClick={() => handleTogglePostLike(expandedPost)}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width="28"
      height="28"
      aria-hidden="true"
    >
      <path
        d="M32 2C18 2 6 14 6 28c0 7.7 5.5 15.3 12 21.6 4.8 4.5 10 8.4 14 12.4 4-4 9.2-8 14-12.4C52.5 43.3 58 35.7 58 28 58 14 46 2 32 2z"
        fill={
          likeStates[expandedPost.post_id]?.liked
            ? '#56ffc7'
            : 'none'
        }
        stroke="#008080"
        strokeWidth="4"
      />

      <path
        d="M32 16c-5-6.5-14-5.5-14 4 0 5 5 10 11 15l3 2.5 3-2.5c6-5 11-10 11-15 0-9.5-9-10.5-14-4z"
        fill={
          likeStates[expandedPost.post_id]?.liked
            ? '#e63946'
            : 'none'
        }
        stroke="#e63946"
        strokeWidth="3"
      />
    </svg>

    <span>
      {likeStates[expandedPost.post_id]?.count ??
        expandedPost.likes ??
        0}
    </span>
  </button>

  <span
    className="profile-expanded-comment-count"
    aria-label={`${expandedPost.comments_count ?? 0} comments`}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#008080"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
    </svg>

    <span>{expandedPost.comments_count ?? 0}</span>
  </span>
</div>
        <div className="profile-post-comments">
          <h3>Comments</h3>

          {commentsError && (
            <p role="alert">{commentsError}</p>
          )}

{postComments
  .filter((comment) => !comment.parent_comment_id)
  .map((comment) => (
    <div
      key={comment.comment_id}
      className="profile-post-comment-thread"
    >
      <div className="profile-post-comment">
        <Avatar
          src={comment.profile_picture || undefined}
          size={30}
        />

        <div className="profile-post-comment-body">
          <div>
            <strong>@{comment.username}</strong>
            <span>: {comment.comment_text}</span>
          </div>

          <div className="profile-post-reply-actions">
            <button
              type="button"
              className="profile-post-reply-button"
              disabled={commentSubmitting}
              onClick={() => {
                setReplyingTo(comment.comment_id);
                setReplyText('');
              }}
            >
              Reply
            </button>

            {((Number(comment.replies_count) > 0 &&
  !loadedReplies[comment.comment_id]) ||
  !!replyErrors[comment.comment_id]) && (
                <button
                  type="button"
                  className="profile-post-reply-button"
                  disabled={
                    !!repliesLoading[comment.comment_id] ||
                    commentSubmitting
                  }
                  onClick={() =>
                    fetchReplies(comment.comment_id)
                  }
                >
                  {repliesLoading[comment.comment_id]
  ? 'Loading replies...'
  : replyErrors[comment.comment_id]
    ? 'Reload replies'
    : `View ${comment.replies_count} replies`}
                </button>
              )}
          </div>
        </div>
      </div>

      {replyErrors[comment.comment_id] && (
        <p
          className="profile-post-comment-error"
          role="alert"
        >
          {replyErrors[comment.comment_id]}
        </p>
      )}

      {loadedReplies[comment.comment_id]?.length > 0 && (
        <div className="profile-post-reply-list">
          {loadedReplies[comment.comment_id].map((reply) => (
            <div
              key={reply.comment_id}
              className="profile-post-comment profile-post-reply"
            >
              <Avatar
                src={reply.profile_picture || undefined}
                size={26}
              />

              <div className="profile-post-comment-body">
                <strong>@{reply.username}</strong>
                <span>: {reply.comment_text}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {replyingTo === comment.comment_id && (
        <form
          className="profile-post-reply-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleAddReply(comment.comment_id);
          }}
        >
          <input
            type="text"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={`Reply to @${comment.username}`}
            aria-label={`Reply to @${comment.username}`}
            disabled={commentSubmitting}
            autoFocus
          />

          <button
            type="submit"
            disabled={!replyText.trim() || commentSubmitting}
          >
            {commentSubmitting ? 'Sending...' : 'Reply'}
          </button>

          <button
            type="button"
            disabled={commentSubmitting}
            onClick={() => {
              setReplyingTo(null);
              setReplyText('');
            }}
          >
            Cancel
          </button>
        </form>
      )}
    </div>
  ))}

          {commentsLoading && <p>Loading comments...</p>}

          {!commentsLoading &&
            !commentsError &&
            postComments.length === 0 && (
              <p>No comments yet.</p>
            )}

          {hasMoreComments && !commentsError && (
            <button
              type="button"
              className="profile-post-load-comments"
              disabled={commentsLoading}
              onClick={() => {
                setCommentsLoading(true);
                setCommentOffset((offset) => offset + 5);
              }}
            >
                            View more comments
            </button>
          )}
        </div>

        <form
          className="profile-post-comment-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleAddComment();
          }}
        >
          {commentSubmitError && (
            <p
              className="profile-post-comment-error"
              role="alert"
            >
              {commentSubmitError}
            </p>
          )}

          <div className="profile-post-comment-input-row">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="What do you think?"
              aria-label="Write a comment"
              disabled={commentSubmitting}
            />

            <button
              type="submit"
              className="profile-post-comment-send"
              disabled={
                !newComment.trim() || commentSubmitting
              }
              aria-label="Post comment"
            >
              {commentSubmitting ? '…' : '➤'}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
)}
      {showEditModal && (
  <div className="modal-overlay">
    <div className="modal-content">
    <button className="close-icon" onClick={() => setShowEditModal(false)}>×</button>

      <h2>Edit Profile</h2>

      <Avatar
  src={
    roamProfile?.profile_picture
      ? `${roamProfile.profile_picture}?t=${profilePicTimestamp}`
      : undefined
  }
  size={100}
  style={{ marginBottom: '12px' }}
/>

<input
  type="file"
  accept="image/*"
  onChange={(e) => {
    setSelectedFile(e.target.files?.[0] || null);
  }}
/>

<div className="profile-picture-actions">
  <button
    type="button"
    className="save-btn profile-picture-upload-btn"
    onClick={handleProfilePicUpload}
    disabled={!selectedFile || removingProfilePicture}
  >
    Upload
  </button>

  {roamProfile?.profile_picture && (
    <button
      type="button"
      className="remove-profile-picture-btn"
      onClick={handleRemoveProfilePicture}
      disabled={removingProfilePicture}
    >
      {removingProfilePicture ? 'Removing...' : 'Remove Picture'}
    </button>
  )}
</div>

      <input
        type="text"
        value={newUsername}
        onChange={(e) => setNewUsername(e.target.value)}
        placeholder="Enter Username"
      />
      {usernameError && (
       <p style={{ color: 'red', fontSize: '14px', marginBottom: '6px' }}>
      {usernameError}
      </p>
      )}

      <button className="save-btn" onClick={handleSaveUsername}>Save Username</button>

      <input
        type="text"
        value={bioInput}
        onChange={(e) => setBioInput(e.target.value)}
        placeholder="Enter Description"
      />
      <button className="save-btn" onClick={handleSaveBio}>Save Bio</button>

    </div>
  </div>
)}
{showFollowModal && (
  <div className="modal-overlay">
    <div className="modal-content">
      <button className="close-icon" onClick={() => setShowFollowModal(false)}>×</button>
      <h2>{followModalType === 'followers' ? 'Followers' : 'Following'}</h2>
      <input
        type="text"
        placeholder="Search username..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="follow-search-input"
      />
      <ul className="follower-list">
        {(() => {
      const list = (followModalType === 'followers' ? followers : following).filter(user =>
      user.username?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (list.length === 0) {
      return <p style={{ padding: '10px', textAlign: 'center' }}>User not found</p>;
      }
      return list.map((user) => (
      <li key={user.user_id} className="follower-item">
       <Avatar
  src={user.profile_picture || undefined}
  size={40}
  className="follower-avatar"
/>

          <span
           className="follower-username-link"
            onClick={() => {
            navigate(`/u/${user.username}`);
           setShowFollowModal(false);
            }}
          >
          @{user.username || 'Unnamed User'}
        </span>
      </li>
       ));
       })()}
      </ul>
      {(followModalType === 'followers' ? followers : following).length === 0 && (
        <p>No {followModalType} found.</p>
      )}
    </div>
  </div>
)}
    </div>
  );
};
export default MyProfilePage;
